import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Plus, Radar, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AccountProfileForm from "@/components/AccountProfileForm";
import LandingSettings from "@/components/LandingSettings";

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-secondary/50" {...props} />
  </div>
);

const EMPTY = { label: "", host: "", api_port: 8728, rest_port: 443, use_tls: true, transport: "api", timeout: 20, link_type: "", username: "admin", password: "", demo_mode: false };
const TRANSPORTS = [
  ["api", "RouterOS API (8728)"],
  ["api-ssl", "RouterOS API-SSL (8729)"],
  ["rest", "REST HTTPS/HTTP"],
];
const LINK_TYPES = ["", "IP Publik", "DDNS", "VPN OpenVPN", "VPN WireGuard", "VPN L2TP/PPTP", "ZeroTier", "Tailscale", "Tunnel lain"];

export default function Settings() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("router");
  const [routers, setRouters] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ping, setPing] = useState(null);
  const [settings, setSettings] = useState(null);
  const [hsProfiles, setHsProfiles] = useState([]);
  const [pkg, setPkg] = useState({ name: "", rate_limit: "", shared_users: 1, session_timeout: "", limit_uptime: "", price_mitra: 3000, price_user: 5000, show_mitra: true, show_user: true });
  const [selfSaldo, setSelfSaldo] = useState({ amount: 0, note: "" });
  const [ftthPkgs, setFtthPkgs] = useState([]);
  const [ftthForm, setFtthForm] = useState({ name: "", rate_limit: "", local_address: "", remote_address: "", price: 150000 });
  const [design, setDesign] = useState({ brand: "ALPHANET", length: 5, case: "upper", charset: "mix" });

  const preview = (() => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const pool = design.charset === "digits" ? "0123456789"
      : design.charset === "letters" ? (design.case === "lower" ? upper.toLowerCase() : upper)
      : (design.case === "lower" ? upper.toLowerCase() : upper) + "0123456789";
    const n = Math.min(Math.max(Number(design.length) || 5, 3), 12);
    return Array.from({ length: n }, (_, i) => pool[(i * 7 + 3) % pool.length]).join("");
  })();

  const saveDesign = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", {
        voucher_design: {
          brand: design.brand,
          length: Math.min(Math.max(Number(design.length) || 5, 3), 12),
          case: design.case,
          charset: design.charset,
        },
      });
      setSettings(data);
      setDesign(data.voucher_design);
      toast.success("Desain voucher disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const load = async () => {
    try {
      const [r, s, hp, fp] = await Promise.all([
        api.get("/routers"), api.get("/settings"),
        api.get("/hotspot/profiles").catch(() => ({ data: [] })),
        api.get("/ftth/packages").catch(() => ({ data: [] })),
      ]);
      setRouters(r.data); setSettings(s.data); setHsProfiles(hp.data); setFtthPkgs(fp.data);
      if (user?.role !== "superadmin" && r.data.length) {
        setEditId((prev) => prev || r.data[0].id);
        setForm((f) => (f.host ? f : { ...EMPTY, ...r.data[0], password: "" }));
      }
      setDesign({ brand: "ALPHANET", length: 5, case: "upper", charset: "mix", ...(s.data.voucher_design || {}) });
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const testConn = async () => {
    setBusy(true); setPing(null);
    try { const { data } = await api.post("/routers/test", { ...form, api_port: Number(form.api_port), rest_port: Number(form.rest_port), timeout: Number(form.timeout) }); setPing(data); data.ok ? toast.success(`Terhubung: ${data.identity || "OK"}`) : toast.error(data.error || "Gagal terhubung"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const detectConn = async () => {
    setBusy(true); setPing(null);
    try {
      const { data } = await api.post("/routers/detect", { ...form, api_port: Number(form.api_port), rest_port: Number(form.rest_port), timeout: Number(form.timeout) });
      setPing(data.ok ? { ok: true, identity: `${data.identity} · ${data.transport}:${data.port}` } : { ok: false, error: data.error });
      if (data.ok) {
        setForm((f) => ({
          ...f, host: data.host, transport: data.transport, use_tls: data.use_tls,
          ...(data.transport === "rest" ? { rest_port: data.port } : { api_port: data.port }),
        }));
        toast.success(`Terdeteksi ${data.transport} port ${data.port} — ${data.identity || "OK"}`);
      } else toast.error(data.error);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitRouter = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { ...form, api_port: Number(form.api_port), rest_port: Number(form.rest_port), timeout: Number(form.timeout) };
    try {
      if (editId) await api.put(`/routers/${editId}`, payload);
      else await api.post("/routers", payload);
      toast.success(editId ? "Router disimpan" : "Router baru ditambahkan & dikaitkan ke akun Anda");
      setForm(EMPTY); setEditId(null);
      await refresh();
      load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const del = async (r) => {
    try { await api.delete(`/routers/${r.id}`); toast.success("Router dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveSettings = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.put("/settings", {
        app_name: settings.app_name,
        voucher_price_mitra: Number(settings.voucher_price_mitra),
        voucher_price_user: Number(settings.voucher_price_user),
        ftth_price: Number(settings.ftth_price),
        admin_wa: settings.admin_wa,
        notif_enabled: !!settings.notif_enabled,
        wifi_name: settings.wifi_name,
        cs_wa: settings.cs_wa,
        teknisi_wa: settings.teknisi_wa || "",
        hotspot_login_url: settings.hotspot_login_url || "",
        voucher_note: settings.voucher_note,
        isolir_profile: settings.isolir_profile,
      });
      setSettings(data); toast.success("Pengaturan disimpan");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const setPlanRow = (i, patch) => {
    const plans = [...(settings.subscription_plans || [])];
    plans[i] = { ...plans[i], ...patch };
    setSettings({ ...settings, subscription_plans: plans });
  };

  const addPlanRow = () => {
    const plans = [...(settings.subscription_plans || []), { key: "", label: "", price: 0, months: 1, desc: "" }];
    setSettings({ ...settings, subscription_plans: plans });
  };

  const removePlanRow = (i) => {
    const plans = (settings.subscription_plans || []).filter((_, idx) => idx !== i);
    setSettings({ ...settings, subscription_plans: plans });
  };

  const setPay = (patch) => setSettings({ ...settings, payment_info: { ...(settings.payment_info || {}), ...patch } });

  const saveSubscription = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const plans = (settings.subscription_plans || []).map((p) => ({
        key: String(p.key || "").trim().toLowerCase(),
        label: String(p.label || "").trim(),
        price: Number(p.price) || 0,
        months: Math.max(1, Number(p.months) || 1),
        desc: p.desc || "",
      }));
      if (!plans.length) { toast.error("Minimal satu paket langganan"); setBusy(false); return; }
      const pay = settings.payment_info || {};
      const { data } = await api.put("/settings", {
        registration_open: !!settings.registration_open,
        reg_wa: settings.reg_wa || "",
        subscription_plans: plans,
        payment_info: {
          bank_name: pay.bank_name || "",
          account_number: pay.account_number || "",
          account_holder: pay.account_holder || "",
          ewallet: pay.ewallet || "",
          note: pay.note || "",
        },
      });
      setSettings(data);
      toast.success("Paket langganan & info pembayaran disimpan");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };


  const createPkg = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/hotspot/profiles", {
        name: pkg.name, rate_limit: pkg.rate_limit, shared_users: Number(pkg.shared_users) || 1,
        session_timeout: pkg.session_timeout, limit_uptime: pkg.limit_uptime,
        price_mitra: Number(pkg.price_mitra), price_user: Number(pkg.price_user),
        show_mitra: pkg.show_mitra, show_user: pkg.show_user,
      });
      toast.success(`Profil ${pkg.name} dibuat di Mikrotik & tersimpan`);
      setPkg({ ...pkg, name: "", rate_limit: "", session_timeout: "", limit_uptime: "" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const savePkgPrices = async (p, patch) => {
    const packages = hsProfiles.map((x) => ({
      profile: x.name,
      price_mitra: Number(x.name === p.name ? (patch.price_mitra ?? x.price_mitra) : x.price_mitra),
      price_user: Number(x.name === p.name ? (patch.price_user ?? x.price_user) : x.price_user),
      show_mitra: x.name === p.name && patch.show_mitra !== undefined ? patch.show_mitra : x.show_mitra,
      show_user: x.name === p.name && patch.show_user !== undefined ? patch.show_user : x.show_user,
      limit_uptime: x.limit_uptime || "",
    }));
    setHsProfiles(hsProfiles.map((x) => (x.name === p.name ? { ...x, ...patch } : x)));
    try { await api.put("/settings", { packages }); toast.success("Harga/visibilitas paket disimpan"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const delPkg = async (p) => {
    try { await api.delete(`/hotspot/profiles/${encodeURIComponent(p.id)}?name=${encodeURIComponent(p.name)}`); toast.success("Profil dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const createFtthPkg = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/ftth/packages", { ...ftthForm, price: Number(ftthForm.price) });
      toast.success(`Paket FTTH ${ftthForm.name} dibuat di Mikrotik`);
      setFtthForm({ ...ftthForm, name: "", rate_limit: "" }); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const setFtthPrice = async (name, price) => {
    try { await api.put(`/ftth/packages/${encodeURIComponent(name)}?price=${Number(price)}`); toast.success(`Harga ${name} disimpan`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const topupSelf = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/me/saldo", { amount: Number(selfSaldo.amount), note: selfSaldo.note });
      toast.success(`Saldo Anda sekarang ${data.saldo.toLocaleString("id-ID")}`);
      setSelfSaldo({ amount: 0, note: "" }); refresh();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const canAddRouter = user?.role === "superadmin" || (!routers.length && !user?.router_id);

  return (
    <Tabs value={tab} onValueChange={setTab} className="fade-up space-y-6">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
        {[["router", "Router Mikrotik"], ["paket", "Paket & Harga Voucher"], ["desain", "Desain Voucher"], ["ftth", "Paket FTTH"], ["umum", "Pengaturan Umum"], ...(user?.role === "superadmin" ? [["langganan", "Paket Langganan Member"], ["landing", "Landing Page"]] : []), ["profil", "Profil & Saldo Saya"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-lg px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-violet-600/20">{l}</TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="router">
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={submitRouter} className="space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="router-form">
            <h3 className="font-display text-lg font-semibold">
              {editId ? "Edit Router" : user?.role === "superadmin" ? "Tambah Router" : canAddRouter ? "Tambah Router Saya" : "Router Saya"}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Label Router" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="router-label-input" />
              <Field label="IP / Host / VPN (boleh ip:port)" required placeholder="10.8.0.1:8728 atau router.ddns.net" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="router-host" />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Metode Koneksi</Label>
                <select
                  className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="router-transport"
                  value={form.transport}
                  onChange={(e) => {
                    const t = e.target.value;
                    setForm({ ...form, transport: t, api_port: t === "api-ssl" ? 8729 : t === "api" ? 8728 : form.api_port });
                  }}
                >
                  {TRANSPORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Jenis Jalur</Label>
                <select
                  className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="router-link-type"
                  value={form.link_type || ""} onChange={(e) => setForm({ ...form, link_type: e.target.value })}
                >
                  {LINK_TYPES.map((l) => <option key={l} value={l}>{l || "— tidak diisi —"}</option>)}
                </select>
              </div>
              <Field label="Port API (8728/8729)" type="number" disabled={form.transport === "rest"} value={form.api_port} onChange={(e) => setForm({ ...form, api_port: e.target.value })} data-testid="router-api-port" />
              <Field label="Port REST (443/80)" type="number" disabled={form.transport !== "rest"} value={form.rest_port} onChange={(e) => setForm({ ...form, rest_port: e.target.value })} data-testid="router-rest-port" />
              <Field label="Timeout (detik, 5–60)" type="number" min="5" max="60" value={form.timeout} onChange={(e) => setForm({ ...form, timeout: e.target.value })} data-testid="router-timeout" />
              <Field label="Username API" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="router-username" />
              <Field label="Password API" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="router-password" />
            </div>
            <p className="text-xs text-muted-foreground">
              Router di balik VPN/tunnel (OpenVPN, WireGuard, ZeroTier, Tailscale) cukup diisi IP VPN-nya. Pastikan service <b>api</b> (8728) atau <b>api-ssl</b> (8729) aktif di router. Sertifikat self-signed API-SSL diabaikan.
            </p>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-3 text-sm">
                <Switch checked={form.use_tls} onCheckedChange={(v) => setForm({ ...form, use_tls: v })} data-testid="router-tls" /> HTTPS (TLS)
              </label>
              <label className="flex items-center gap-3 text-sm">
                <Switch checked={form.demo_mode} onCheckedChange={(v) => setForm({ ...form, demo_mode: v })} data-testid="router-demo" /> Mode Demo (simulasi)
              </label>
            </div>
            {ping && (
              <div className={`rounded-xl border p-3 text-xs ${ping.ok ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`} data-testid="ping-result">
                {ping.ok ? <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Terhubung — {ping.identity}</span> : `Gagal: ${ping.error}`}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {user?.role !== "superadmin" && (
                canAddRouter ? (
                  <p className="w-full rounded-xl border border-cyan-500/30 bg-cyan-500/5 px-4 py-3 text-xs text-cyan-300" data-testid="router-admin-add-note">
                    Akun admin Anda belum terkait router mana pun. Isi data di bawah lalu simpan untuk menambahkan router Anda — setiap admin hanya boleh memiliki 1 router.
                  </p>
                ) : (
                  <p className="w-full rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-300" data-testid="router-admin-note">
                    Admin hanya boleh memiliki 1 router. Anda dapat mengubah data router yang sudah ada; penambahan router lain dilakukan oleh Superadmin.
                  </p>
                )
              )}
              <Button type="button" variant="outline" className="rounded-full" onClick={detectConn} disabled={busy} data-testid="router-detect"><Radar className="mr-1 h-4 w-4" /> Deteksi Otomatis</Button>
              <Button type="button" variant="outline" className="rounded-full" onClick={testConn} disabled={busy} data-testid="router-test"><Radar className="mr-1 h-4 w-4" /> Test Ping API</Button>
              <Button type="submit" disabled={busy || (!editId && !canAddRouter)} data-testid="router-save" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> {editId ? "Simpan Perubahan" : "Simpan Router"}</>}
              </Button>
              {editId && <Button type="button" variant="ghost" onClick={() => { setEditId(null); setForm(EMPTY); }}>Batal</Button>}
            </div>
          </form>

          <div className="space-y-3">
            {routers.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-2xl border border-border bg-card p-5" data-testid={`router-item-${r.id}`}>
                <div>
                  <p className="font-display font-semibold">{r.label} {r.demo_mode ? <span className="ml-1 text-[10px] uppercase tracking-widest text-violet-400">demo</span> : null}</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {r.host} · {r.transport === "rest" ? `rest ${r.rest_port}` : `${r.transport || "api"} ${r.api_port}`} · {r.username}
                    {r.link_type ? ` · ${r.link_type}` : ""} · timeout {r.timeout || 20}s
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEditId(r.id); setForm({ ...EMPTY, ...r, password: "" }); }} data-testid={`router-edit-${r.id}`}>Edit</Button>
                  {user?.role === "superadmin" && (
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(r)} data-testid={`router-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            ))}
            {!routers.length && <p className="text-sm text-muted-foreground">Belum ada router.</p>}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="paket">
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={createPkg} className="space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="pkg-form">
            <h3 className="font-display text-lg font-semibold">Tambah User Profil Hotspot</h3>
            <p className="text-xs text-muted-foreground">Profil dibuat langsung di Mikrotik dan harganya tersimpan di aplikasi.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nama Profil" required value={pkg.name} onChange={(e) => setPkg({ ...pkg, name: e.target.value })} data-testid="pkg-name" />
              <Field label="Rate Limit (mis. 3M/3M)" value={pkg.rate_limit} onChange={(e) => setPkg({ ...pkg, rate_limit: e.target.value })} data-testid="pkg-rate" />
              <Field label="Shared Users" type="number" value={pkg.shared_users} onChange={(e) => setPkg({ ...pkg, shared_users: e.target.value })} data-testid="pkg-shared" />
              <Field label="Session Timeout (mis. 1d)" value={pkg.session_timeout} onChange={(e) => setPkg({ ...pkg, session_timeout: e.target.value })} data-testid="pkg-session" />
              <Field label="Limit Uptime Voucher" value={pkg.limit_uptime} onChange={(e) => setPkg({ ...pkg, limit_uptime: e.target.value })} data-testid="pkg-limit" />
              <Field label="Harga untuk Mitra" type="number" value={pkg.price_mitra} onChange={(e) => setPkg({ ...pkg, price_mitra: e.target.value })} data-testid="pkg-price-mitra" />
              <Field label="Harga untuk User" type="number" value={pkg.price_user} onChange={(e) => setPkg({ ...pkg, price_user: e.target.value })} data-testid="pkg-price-user" />
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-3 text-sm"><Switch checked={pkg.show_mitra} onCheckedChange={(v) => setPkg({ ...pkg, show_mitra: v })} data-testid="pkg-show-mitra" /> Tampil di Mitra</label>
              <label className="flex items-center gap-3 text-sm"><Switch checked={pkg.show_user} onCheckedChange={(v) => setPkg({ ...pkg, show_user: v })} data-testid="pkg-show-user" /> Tampil di User</label>
            </div>
            <Button type="submit" disabled={busy} data-testid="pkg-save" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Simpan ke Mikrotik</>}
            </Button>
          </form>

          <div className="space-y-3" data-testid="pkg-list">
            {hsProfiles.map((p) => (
              <div key={p.name} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-display font-semibold">{p.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{p.rate_limit || "tanpa limit"} · shared {p.shared_users || 1}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delPkg(p)} data-testid={`pkg-delete-${p.name}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Field label="Harga Mitra" type="number" defaultValue={p.price_mitra} onBlur={(e) => savePkgPrices(p, { price_mitra: e.target.value })} data-testid={`pkg-mitra-${p.name}`} />
                  <Field label="Harga User" type="number" defaultValue={p.price_user} onBlur={(e) => savePkgPrices(p, { price_user: e.target.value })} data-testid={`pkg-user-${p.name}`} />
                </div>
                <div className="mt-4 flex flex-wrap gap-5 text-xs">
                  <label className="flex items-center gap-2"><Switch checked={!!p.show_mitra} onCheckedChange={(v) => savePkgPrices(p, { show_mitra: v })} data-testid={`pkg-vis-mitra-${p.name}`} /> Tampil di Mitra</label>
                  <label className="flex items-center gap-2"><Switch checked={!!p.show_user} onCheckedChange={(v) => savePkgPrices(p, { show_user: v })} data-testid={`pkg-vis-user-${p.name}`} /> Tampil di User</label>
                </div>
              </div>
            ))}
            {!hsProfiles.length && <p className="text-sm text-muted-foreground">Mengambil profil hotspot dari Mikrotik…</p>}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="desain">
        {settings && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="voucher-design-form">
              <h3 className="font-display text-lg font-semibold">Desain Voucher</h3>
              <p className="text-xs text-muted-foreground">Menentukan brand pada kartu cetak serta pola kode voucher yang dibuat (Add User, Generate, dan portal Mitra/User).</p>
              <Field
                label="Nama / Brand" value={design.brand} data-testid="vd-brand"
                onChange={(e) => setDesign({ ...design, brand: e.target.value })}
              />
              <Field
                label="Jumlah Karakter (3-12)" type="number" min="3" max="12" value={design.length} data-testid="vd-length"
                onChange={(e) => setDesign({ ...design, length: e.target.value })}
              />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Huruf</Label>
                <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="vd-case" value={design.case} onChange={(e) => setDesign({ ...design, case: e.target.value })}>
                  <option value="upper">HURUF BESAR</option>
                  <option value="lower">huruf kecil</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Isi Kode</Label>
                <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="vd-charset" value={design.charset} onChange={(e) => setDesign({ ...design, charset: e.target.value })}>
                  <option value="mix">Huruf + Angka (mix)</option>
                  <option value="letters">Hanya Huruf</option>
                  <option value="digits">Hanya Angka</option>
                </select>
              </div>
              <Button onClick={saveDesign} disabled={busy} data-testid="vd-save" className="rounded-full bg-gradient-to-r from-sky-400 via-violet-400 to-fuchsia-400 text-white">Simpan Desain</Button>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Pratinjau Kartu</p>
              <div className="mx-auto mt-5 w-[220px] rounded-xl border border-violet-400/50 bg-gradient-to-br from-sky-400/15 via-card to-fuchsia-400/15 p-4 text-center" data-testid="vd-preview">
                <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{design.brand || "ALPHANET"}</p>
                <p className="mt-3 font-mono text-2xl font-extrabold tracking-[0.12em]">{preview}</p>
                <div className="mt-3 flex justify-between font-mono text-[10px] text-muted-foreground">
                  <span>Rp 5.000</span><span>1d</span>
                </div>
              </div>
              <p className="mt-4 text-center text-xs text-muted-foreground">Kode contoh dihasilkan dari pengaturan di samping.</p>
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="ftth">
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={createFtthPkg} className="space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="ftth-pkg-form">
            <h3 className="font-display text-lg font-semibold">Buat Paket FTTH / Profil PPPoE</h3>
            <p className="text-xs text-muted-foreground">Profil PPPoE dibuat langsung di Mikrotik, harga bulanannya tersimpan di aplikasi.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nama Paket" required value={ftthForm.name} onChange={(e) => setFtthForm({ ...ftthForm, name: e.target.value })} data-testid="ftth-pkg-name" />
              <Field label="Rate Limit (mis. 10M/10M)" value={ftthForm.rate_limit} onChange={(e) => setFtthForm({ ...ftthForm, rate_limit: e.target.value })} data-testid="ftth-pkg-rate" />
              <Field label="Local Address" value={ftthForm.local_address} onChange={(e) => setFtthForm({ ...ftthForm, local_address: e.target.value })} data-testid="ftth-pkg-local" />
              <Field label="Remote Address / Pool" value={ftthForm.remote_address} onChange={(e) => setFtthForm({ ...ftthForm, remote_address: e.target.value })} data-testid="ftth-pkg-remote" />
              <Field label="Harga / Bulan" type="number" value={ftthForm.price} onChange={(e) => setFtthForm({ ...ftthForm, price: e.target.value })} data-testid="ftth-pkg-price" />
            </div>
            <Button type="submit" disabled={busy} data-testid="ftth-pkg-save" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Simpan ke Mikrotik</>}
            </Button>
          </form>

          <div className="space-y-3" data-testid="ftth-pkg-list">
            {ftthPkgs.map((p) => (
              <div key={p.name} className="rounded-2xl border border-border bg-card p-5">
                <p className="font-display font-semibold">{p.name}</p>
                <p className="font-mono text-xs text-muted-foreground">{p.rate_limit || "tanpa limit"} · {p.local_address || "-"}</p>
                <div className="mt-3">
                  <Field label="Harga / Bulan" type="number" defaultValue={p.price} onBlur={(e) => setFtthPrice(p.name, e.target.value)} data-testid={`ftth-price-${p.name}`} />
                </div>
              </div>
            ))}
            {!ftthPkgs.length && <p className="text-sm text-muted-foreground">Mengambil profil PPPoE dari Mikrotik…</p>}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="umum">
        {settings && (
          <form onSubmit={saveSettings} className="max-w-xl space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="settings-form">
            <h3 className="font-display text-lg font-semibold">Pengaturan Umum</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nama Aplikasi" value={settings.app_name} onChange={(e) => setSettings({ ...settings, app_name: e.target.value })} data-testid="set-appname" />
              <Field label="WA Admin" value={settings.admin_wa} onChange={(e) => setSettings({ ...settings, admin_wa: e.target.value })} data-testid="set-wa" />
              <Field label="Nama WiFi (kartu voucher)" value={settings.wifi_name || ""} onChange={(e) => setSettings({ ...settings, wifi_name: e.target.value })} data-testid="set-wifi" />
              <Field label="WA CS (kartu voucher)" value={settings.cs_wa || ""} onChange={(e) => setSettings({ ...settings, cs_wa: e.target.value })} data-testid="set-cs" />
              <Field label="WA Teknisi (lapor gangguan)" value={settings.teknisi_wa || ""} onChange={(e) => setSettings({ ...settings, teknisi_wa: e.target.value })} data-testid="set-teknisi-wa" />
              <Field label="Alamat Login Hotspot (QR)" placeholder="http://192.168.88.1/login" value={settings.hotspot_login_url || ""} onChange={(e) => setSettings({ ...settings, hotspot_login_url: e.target.value })} data-testid="set-hotspot-url" />
              <Field label="Nama Profil Isolir" value={settings.isolir_profile || ""} onChange={(e) => setSettings({ ...settings, isolir_profile: e.target.value })} data-testid="set-isolir" />
              <Field label="Cara Pakai (kartu voucher)" value={settings.voucher_note || ""} onChange={(e) => setSettings({ ...settings, voucher_note: e.target.value })} data-testid="set-note" />
            </div>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={!!settings.notif_enabled} onCheckedChange={(v) => setSettings({ ...settings, notif_enabled: v })} data-testid="set-notif" /> Notifikasi real-time
            </label>
            <Button type="submit" disabled={busy} data-testid="settings-save" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">Simpan</Button>
          </form>
        )}
      </TabsContent>

      <TabsContent value="langganan">
        {settings && (
          <form onSubmit={saveSubscription} className="space-y-6" data-testid="subscription-settings-form">
            <div className="space-y-5 rounded-2xl border border-border bg-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display text-lg font-semibold">Paket Langganan Member</h3>
                <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addPlanRow} data-testid="plan-add">
                  + Tambah Paket
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Paket ini dipakai di halaman pendaftaran admin baru (/daftar) dan halaman perpanjangan member. Kode paket dipakai sistem, jangan diubah bila sudah dipakai member.
              </p>
              <div className="space-y-4">
                {(settings.subscription_plans || []).map((p, i) => (
                  <div key={i} className="grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-2 lg:grid-cols-5" data-testid={`plan-row-${i}`}>
                    <Field label="Kode" value={p.key || ""} onChange={(e) => setPlanRow(i, { key: e.target.value })} data-testid={`plan-key-${i}`} />
                    <Field label="Nama Paket" value={p.label || ""} onChange={(e) => setPlanRow(i, { label: e.target.value })} data-testid={`plan-label-${i}`} />
                    <Field label="Harga (Rp)" type="number" value={p.price ?? 0} onChange={(e) => setPlanRow(i, { price: e.target.value })} data-testid={`plan-price-${i}`} />
                    <Field label="Lama (bulan)" type="number" value={p.months ?? 1} onChange={(e) => setPlanRow(i, { months: e.target.value })} data-testid={`plan-months-${i}`} />
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Field label="Keterangan" value={p.desc || ""} onChange={(e) => setPlanRow(i, { desc: e.target.value })} data-testid={`plan-desc-${i}`} />
                      </div>
                      <Button type="button" size="icon" variant="ghost" className="mb-1 h-9 w-9" onClick={() => removePlanRow(i)} data-testid={`plan-remove-${i}`}>
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-5 rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-semibold">Info Pembayaran</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Nama Bank" value={settings.payment_info?.bank_name || ""} onChange={(e) => setPay({ bank_name: e.target.value })} placeholder="BRI" data-testid="pay-bank" />
                  <Field label="Nomor Rekening" value={settings.payment_info?.account_number || ""} onChange={(e) => setPay({ account_number: e.target.value })} data-testid="pay-account" />
                  <Field label="Nama Pemilik Rekening" value={settings.payment_info?.account_holder || ""} onChange={(e) => setPay({ account_holder: e.target.value })} data-testid="pay-holder" />
                  <Field label="E-Wallet (opsional)" value={settings.payment_info?.ewallet || ""} onChange={(e) => setPay({ ewallet: e.target.value })} placeholder="DANA/OVO 0812..." data-testid="pay-ewallet" />
                </div>
                <Field label="Catatan Pembayaran" value={settings.payment_info?.note || ""} onChange={(e) => setPay({ note: e.target.value })} data-testid="pay-note" />
              </div>

              <div className="space-y-5 rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-semibold">Pendaftaran</h3>
                <label className="flex items-center gap-3 text-sm">
                  <Switch checked={settings.registration_open !== false} onCheckedChange={(v) => setSettings({ ...settings, registration_open: v })} data-testid="reg-open-switch" />
                  Buka pendaftaran admin baru di halaman depan
                </label>
                <Field
                  label="WA Konfirmasi Pendaftaran" value={settings.reg_wa || ""} onChange={(e) => setSettings({ ...settings, reg_wa: e.target.value })}
                  placeholder="kosong = pakai WA Admin" data-testid="reg-wa"
                />
                <p className="text-xs text-muted-foreground">
                  Nomor ini yang dituju pendaftar saat menekan tombol “Konfirmasi via WhatsApp”. Kosongkan untuk memakai WA Admin di Pengaturan Umum.
                </p>
              </div>
            </div>

            <Button type="submit" disabled={busy} data-testid="subscription-settings-save" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
              Simpan Paket & Pembayaran
            </Button>
          </form>
        )}
      </TabsContent>

      <TabsContent value="landing">
        {user?.role === "superadmin" && <LandingSettings />}
      </TabsContent>


      <TabsContent value="profil">
        <div className="grid gap-6 lg:grid-cols-2">
          <AccountProfileForm />

          <form onSubmit={topupSelf} className="max-w-md space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="self-saldo-form">
            <h3 className="font-display text-lg font-semibold">Isi Saldo Saya</h3>
            <p className="text-xs text-muted-foreground">Saldo saat ini: <span className="font-mono">{Number(user?.saldo || 0).toLocaleString("id-ID")}</span>. Setiap penambahan tercatat di audit trail.</p>
            <Field label="Nominal (boleh minus)" type="number" value={selfSaldo.amount} onChange={(e) => setSelfSaldo({ ...selfSaldo, amount: e.target.value })} data-testid="self-saldo-amount" />
            <Field label="Catatan" value={selfSaldo.note} onChange={(e) => setSelfSaldo({ ...selfSaldo, note: e.target.value })} data-testid="self-saldo-note" />
            <Button type="submit" disabled={busy} data-testid="self-saldo-save" className="rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white">Tambah Saldo</Button>
          </form>
        </div>
      </TabsContent>
    </Tabs>
  );
}
